import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  computeLiveMapDateRangeFromHours,
  DEFAULT_LIVE_MAP_HOURS_BACK,
  DEFAULT_LIVE_MAP_HOURS_FORWARD,
  liveMapHoursBackOverrideAtom,
  liveMapHoursForwardOverrideAtom,
  Log,
  resolveLiveMapHours,
  topCalendarDatesAtomFamily,
  useFormats,
  useIsMobile,
  useSystemParams,
  useTimezone,
  type RydeUiUnit,
} from "@ryde/shared";

import {
  HoursRangeDatePreviewStyled,
  HoursRangeSeparatorStyled,
  HoursRangeWrapper,
} from "./live-map.hours-range.widget.styled";
import { LiveMapHoursNumberField } from "./parts/live-map.hours-number-field";

const LIVE_MAP_DATE_PREVIEW_REFRESH_MS = 60_000;

interface ILiveMapHoursRangeWidget {
  entity: RydeUiUnit;
}

export const LiveMapHoursRangeWidget: FC<ILiveMapHoursRangeWidget> = ({
  entity,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { timezone } = useTimezone();
  const { formatDateTimeNormalized } = useFormats();
  const { liveMapHoursBack, liveMapHoursForward } = useSystemParams();
  const [datePreviewTick, setDatePreviewTick] = useState(0);
  const [hoursBackOverride, setHoursBackOverride] = useRecoilState(
    liveMapHoursBackOverrideAtom,
  );
  const [hoursForwardOverride, setHoursForwardOverride] = useRecoilState(
    liveMapHoursForwardOverrideAtom,
  );
  const setCalendarDates = useSetRecoilState(
    topCalendarDatesAtomFamily(entity),
  );

  const effectiveHoursBack = useMemo(
    () =>
      resolveLiveMapHours({
        overrideValue: hoursBackOverride,
        systemParamValue: liveMapHoursBack,
        fallback: DEFAULT_LIVE_MAP_HOURS_BACK,
      }),
    [hoursBackOverride, liveMapHoursBack],
  );

  const effectiveHoursForward = useMemo(
    () =>
      resolveLiveMapHours({
        overrideValue: hoursForwardOverride,
        systemParamValue: liveMapHoursForward,
        fallback: DEFAULT_LIVE_MAP_HOURS_FORWARD,
      }),
    [hoursForwardOverride, liveMapHoursForward],
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setDatePreviewTick(tick => tick + 1);
    }, LIVE_MAP_DATE_PREVIEW_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!timezone) {
      return;
    }

    const { date1, date2 } = computeLiveMapDateRangeFromHours({
      timezone,
      hoursBack: effectiveHoursBack,
      hoursForward: effectiveHoursForward,
    });
    setCalendarDates({ date1, date2 });
  }, [effectiveHoursBack, effectiveHoursForward, timezone, setCalendarDates]);

  const dateRangePreview = useMemo(() => {
    if (!timezone) {
      return undefined;
    }

    const range = computeLiveMapDateRangeFromHours({
      timezone,
      hoursBack: effectiveHoursBack,
      hoursForward: effectiveHoursForward,
    });
    Log.debug("Live map calendar dates preview refreshed", {
      tick: datePreviewTick,
      ...range,
    });
    return range;
  }, [timezone, effectiveHoursBack, effectiveHoursForward, datePreviewTick]);

  const increaseHoursLabel = t("liveMap.hoursRange.increaseHours");
  const decreaseHoursLabel = t("liveMap.hoursRange.decreaseHours");

  return (
    <HoursRangeWrapper>
      {!isMobile && <span>{t("liveMap.hoursRange.from")}</span>}
      <LiveMapHoursNumberField
        value={effectiveHoursBack}
        onChange={setHoursBackOverride}
        dataE2e="live-map-hours-back"
        inputAriaLabel={t("liveMap.hoursRange.hoursBack")}
        increaseAriaLabel={increaseHoursLabel}
        decreaseAriaLabel={decreaseHoursLabel}
      />
      <span>{t("liveMap.hoursRange.hoursBack")}</span>
      {!isMobile && (
        <HoursRangeSeparatorStyled aria-hidden>
          {t("liveMap.hoursRange.rangeSeparator")}
        </HoursRangeSeparatorStyled>
      )}
      {!isMobile && <span>{t("liveMap.hoursRange.upTo")}</span>}
      <LiveMapHoursNumberField
        value={effectiveHoursForward}
        onChange={setHoursForwardOverride}
        dataE2e="live-map-hours-forward"
        inputAriaLabel={t("liveMap.hoursRange.hoursForward")}
        increaseAriaLabel={increaseHoursLabel}
        decreaseAriaLabel={decreaseHoursLabel}
      />
      <span>{t("liveMap.hoursRange.hoursForward")}</span>
      {dateRangePreview && (
        <HoursRangeDatePreviewStyled>
          <span>{formatDateTimeNormalized(dateRangePreview.date1)}</span>
          <span className="dash">–</span>
          <span>{formatDateTimeNormalized(dateRangePreview.date2)}</span>
        </HoursRangeDatePreviewStyled>
      )}
    </HoursRangeWrapper>
  );
};
